import { ArrowUpRight, Bot, CornerDownRight, Inbox, Radio } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// Scripted replay of the real flow: create inbox → mail arrives → push event
// → agent replies in thread. All names and addresses are fictional.

const ADDRESS = 'support@k7f3.inboxkit.dev';

const CREATE_CMD = `POST /v1/inboxes  {"local_part":"support"}`;
const REPLY_CMD = `POST /v1/inboxes/${ADDRESS}/messages/1042/reply`;

interface Frame {
  at: number; // ms from loop start
  step: number;
}

// step: 0 idle, 1 typing create, 2 inbox live, 3 mail in, 4 event pushed,
// 5 typing reply, 6 reply sent
const FRAMES: Frame[] = [
  { at: 0, step: 0 },
  { at: 500, step: 1 },
  { at: 2300, step: 2 },
  { at: 3600, step: 3 },
  { at: 4600, step: 4 },
  { at: 6000, step: 5 },
  { at: 8200, step: 6 },
];
const RAIL = [
  { label: 'create', from: 2 },
  { label: 'receive', from: 3 },
  { label: 'push', from: 4 },
  { label: 'reply', from: 6 },
];
const LOOP_MS = 12500;
const FINAL_STEP = 6;

function usePrefersReducedMotion() {
  // false on the first render so the pre-rendered HTML hydrates; read after mount.
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** Drives the step timeline while the demo is on screen. */
function useTimeline(active: boolean, reduced: boolean) {
  const [step, setStep] = useState(reduced ? FINAL_STEP : 0);
  useEffect(() => {
    if (reduced) {
      setStep(FINAL_STEP);
      return;
    }
    if (!active) return;
    let timers: number[] = [];
    const run = () => {
      timers = FRAMES.map((f) => window.setTimeout(() => setStep(f.step), f.at));
      timers.push(window.setTimeout(run, LOOP_MS));
    };
    run();
    return () => timers.forEach(clearTimeout);
  }, [active, reduced]);
  return step;
}

function useTyped(text: string, typing: boolean, done: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (done) {
      setCount(text.length);
      return;
    }
    if (!typing) {
      setCount(0);
      return;
    }
    setCount(0);
    const id = window.setInterval(() => {
      setCount((c) => (c >= text.length ? c : c + 2));
    }, 24);
    return () => clearInterval(id);
  }, [text, typing, done]);
  return text.slice(0, count);
}

function Line({ show, children, className = '' }: { show: boolean; children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`transition-all duration-500 ${show ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0'} ${className}`}
    >
      {children}
    </div>
  );
}

export function HeroDemo() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setVisible(Boolean(entry?.isIntersecting)), {
      threshold: 0.25,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const step = useTimeline(visible, reduced);
  const createTyped = useTyped(CREATE_CMD, step === 1, step >= 2);
  const replyTyped = useTyped(REPLY_CMD, step === 5, step >= 6);

  return (
    <div ref={ref} className="relative">
      {/* Glow behind the window */}
      <div
        aria-hidden
        className="spectrum absolute inset-x-[12%] -bottom-6 -z-10 h-24 rounded-full opacity-35 blur-3xl"
      />
      <div
        role="img"
        aria-label="Animated demo: an agent creates an inbox, receives an email, gets a message.received event, and replies in the same thread."
        className="overflow-hidden rounded-2xl border border-line-strong bg-ink-900/90 shadow-[0_40px_120px_-20px_rgb(0_0_0/0.8)] backdrop-blur"
      >
        {/* Window chrome */}
        <div className="flex items-center gap-2 border-b border-line bg-ink-850 px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          <span className="ml-3 font-mono text-[11px] tracking-wide text-white/40">agent.ts — live</span>
          <span className="ml-auto flex items-center gap-2 font-mono text-[11px] text-white/45">
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-signal" />
            api.inboxkit.dev
          </span>
        </div>

        <div className="grid md:grid-cols-[1.05fr_1fr]">
          {/* Terminal */}
          <div className="min-h-[230px] border-b border-line p-5 font-mono text-[12.5px] leading-6 md:min-h-[340px] md:border-r md:border-b-0">
            <div className="text-white/35"># give the agent its own address</div>
            <div className="break-all text-white/90">
              <span className="text-signal">❯ </span>
              <span className={step === 1 ? 'caret' : ''}>{createTyped}</span>
            </div>
            <Line show={step >= 2} className="mt-1">
              <span className="text-signal-dim">201 Created</span>
              <span className="text-white/35"> · no DNS needed</span>
              <div className="text-white/70">
                {'{ '}
                <span className="text-syntax-key">"address"</span>: <span className="text-syntax-str">"{ADDRESS}"</span>
                {' }'}
              </div>
            </Line>

            <Line show={step >= 4} className="mt-4">
              <div className="text-white/35"># pushed over webhook + WebSocket</div>
              <div className="text-white/70">
                <span className="text-syntax-kw">event</span> <span className="text-syntax-str">message.received</span>
                <span className="text-white/35"> thread_id=</span>
                <span className="text-syntax-fn">t_9f2c</span>
              </div>
            </Line>

            <Line show={step >= 5} className="mt-4">
              <div className="text-white/35"># reply in the same thread</div>
              <div className="break-all text-white/90">
                <span className="text-signal">❯ </span>
                <span className={step === 5 ? 'caret' : ''}>{replyTyped}</span>
              </div>
            </Line>
            <Line show={step >= 6} className="mt-1">
              <span className="text-signal-dim">200 OK</span>
              <span className="text-white/35"> · In-Reply-To + References set</span>
            </Line>
          </div>

          {/* Inbox */}
          <div className="flex min-h-[260px] flex-col bg-ink-950/40 md:min-h-[340px]">
            <div className="flex items-center gap-2.5 border-b border-line px-5 py-3.5">
              <Inbox className="h-4 w-4 text-white/50" aria-hidden />
              <span
                className={`truncate font-mono text-[12px] transition-colors duration-500 ${step >= 2 ? 'text-white' : 'text-white/25'}`}
              >
                {step >= 2 ? ADDRESS : 'no inbox yet'}
              </span>
              {step >= 2 && (
                <span className="ml-auto animate-fade-up rounded-full border border-signal/30 bg-signal/10 px-2 py-0.5 font-mono text-[10px] text-signal">
                  LIVE
                </span>
              )}
            </div>

            <div className="flex flex-1 flex-col gap-3 p-4">
              {step >= 2 && step < 3 && (
                <div className="m-auto animate-fade-up text-center font-mono text-[11px] text-white/35">
                  waiting for mail…
                </div>
              )}

              <Line show={step >= 3}>
                <div className="rounded-xl border border-line bg-ink-800/80 p-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-gradient-to-br from-sky-400/70 to-indigo-500/70 text-[11px] font-semibold text-white">
                      M
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-[12.5px] font-medium text-white">Mira Quell</div>
                      <div className="truncate font-mono text-[10.5px] text-white/40">mira@northwind.example</div>
                    </div>
                    <span className="ml-auto font-mono text-[10px] text-white/35">now</span>
                  </div>
                  <div className="mt-2.5 text-[12.5px] font-medium text-white/90">Invoice #1042 — paid twice?</div>
                  <div className="mt-0.5 line-clamp-2 text-[12px] leading-5 text-white/50">
                    Hi, our card was charged twice for March. Can you refund one of the payments?
                  </div>
                  <div className="mt-2.5 flex items-center gap-1.5 font-mono text-[10px] text-white/35">
                    <Radio className="h-3 w-3" aria-hidden />
                    <span className={step >= 4 ? 'text-signal' : ''}>
                      {step >= 4 ? 'webhook.delivered 200 · ws push' : 'delivering…'}
                    </span>
                  </div>
                </div>
              </Line>

              <Line show={step >= 6} className="pl-6">
                <div className="relative rounded-xl border border-signal/25 bg-signal/[0.06] p-3.5">
                  <CornerDownRight className="absolute -left-5 top-3 h-4 w-4 text-white/25" aria-hidden />
                  <div className="flex items-center gap-2.5">
                    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-signal/15 text-signal">
                      <Bot className="h-3.5 w-3.5" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-[12.5px] font-medium text-white">Support agent</div>
                      <div className="truncate font-mono text-[10.5px] text-white/40">{ADDRESS}</div>
                    </div>
                    <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-signal" aria-hidden />
                  </div>
                  <div className="mt-2.5 text-[12px] leading-5 text-white/70">
                    Hi Mira — you are right. I refunded the duplicate charge. It shows in 3–5 business days.
                  </div>
                </div>
              </Line>
            </div>
          </div>
        </div>

        {/* Timeline rail */}
        <div className="flex items-center gap-1.5 border-t border-line bg-ink-850 px-4 py-2.5">
          {RAIL.map(({ label, from }) => {
            const on = step >= from;
            return (
              <div key={label} className="flex flex-1 items-center gap-2">
                <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/[0.07]">
                  <div
                    className={`h-full rounded-full bg-signal transition-[width] duration-700 ease-out ${on ? 'w-full' : 'w-0'}`}
                  />
                </div>
                <span
                  className={`font-mono text-[10px] uppercase tracking-wider transition-colors ${on ? 'text-white/70' : 'text-white/25'}`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
